import type { Policy, CreatePolicyInput, PolicyLabel, WorkflowType } from '@communities/content-management/domain';

import type { DialogFormData } from '../types';

/**
 * Transform dialog form data to policy input for API calls
 */
export function transformDialogDataToPolicyInput(formData: DialogFormData, workflowType: WorkflowType): CreatePolicyInput {
  // Transform text labels
  const textPolicyLabels: PolicyLabel[] = formData.textLabels.textLabels.map(label => ({
    labelId: label.labelId,
    severity: label.severity,
  }));

  // Transform image labels
  const imagePolicyLabels: PolicyLabel[] = formData.imageLabels.imageLabels.map(label => ({
    labelId: label.labelId,
    scoreThreshold: label.scoreThreshold,
  }));

  return {
    name: formData.policyInfo.name.trim(),
    description: formData.policyInfo.description?.trim() || undefined,
    isEnabled: formData.policyInfo.isEnabled,
    labels: [...textPolicyLabels, ...imagePolicyLabels],
    workflowType,
  };
}

/**
 * Transform policy to dialog form data for editing
 */
export function transformPolicyToDialogData(policy: Policy): DialogFormData {
  // Separate text and image labels
  const textLabels = policy.labels
    .filter(label => typeof label.severity === 'number' && label.severity !== undefined)
    .map(label => ({
      labelId: label.labelId,
      severity: label.severity as number,
    }));

  const imageLabels = policy.labels
    .filter(label => typeof label.scoreThreshold === 'number' && label.scoreThreshold !== undefined)
    .map(label => ({
      labelId: label.labelId,
      scoreThreshold: label.scoreThreshold as number,
    }));

  return {
    policyInfo: {
      name: policy.name,
      description: policy.description || '',
      isEnabled: policy.isEnabled,
    },
    textLabels: {
      textLabels,
    },
    imageLabels: {
      imageLabels,
    },
    review: {
      isValid: true,
      warnings: [],
    },
  };
}

/**
 * Validate dialog form data for completeness
 */
export function validateDialogFormData(formData: DialogFormData): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Validate policy info
  if (!formData.policyInfo.name.trim()) {
    errors.push('Policy name is required');
  }

  if (formData.policyInfo.name.trim().length < 3) {
    errors.push('Policy name must be at least 3 characters long');
  }

  // Labels are now optional - no longer require at least one label
  // Users can create policies without labels and add them later

  // Validate text labels
  formData.textLabels.textLabels.forEach((label, index) => {
    if (!label.labelId.trim()) {
      errors.push(`Text label ${index + 1}: Label ID is required`);
    }

    if (label.severity < 1 || label.severity > 5) {
      errors.push(`Text label ${index + 1}: Severity must be between 1 and 5`);
    }
  });

  // Validate image labels
  formData.imageLabels.imageLabels.forEach((label, index) => {
    if (!label.labelId.trim()) {
      errors.push(`Image label ${index + 1}: Label ID is required`);
    }

    if (label.scoreThreshold < 0 || label.scoreThreshold > 100) {
      errors.push(`Image label ${index + 1}: Score threshold must be between 0 and 100`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Warning categories for better organization
 */
export enum WarningCategory {
  CONFIGURATION = 'configuration',
  BEST_PRACTICES = 'best_practices',
  ACCESSIBILITY = 'accessibility',
}

/**
 * Warning interface for better structure
 */
export interface PolicyWarning {
  category: WarningCategory;
  message: string;
  suggestion?: string;
}

/**
 * Check for configuration-related warnings
 */
function getConfigurationWarnings(formData: DialogFormData): PolicyWarning[] {
  const warnings: PolicyWarning[] = [];

  if (formData.policyInfo.name.length > 50) {
    warnings.push({
      category: WarningCategory.CONFIGURATION,
      message: 'Policy name is quite long.',
      suggestion: 'Consider a shorter, more descriptive name for better readability.',
    });
  }

  if (!formData.policyInfo.description.trim()) {
    warnings.push({
      category: WarningCategory.CONFIGURATION,
      message: 'No description provided.',
      suggestion: 'Adding a description will help others understand the purpose of this policy.',
    });
  }

  if (formData.textLabels.textLabels.length > 10) {
    warnings.push({
      category: WarningCategory.CONFIGURATION,
      message: 'This policy has many text labels.',
      suggestion: 'Consider grouping related labels into separate policies for better maintainability.',
    });
  }

  if (formData.imageLabels.imageLabels.length > 10) {
    warnings.push({
      category: WarningCategory.CONFIGURATION,
      message: 'This policy has many image labels.',
      suggestion: 'Consider grouping related labels into separate policies for better maintainability.',
    });
  }

  return warnings;
}

/**
 * Check for duplicate labels across types
 */
function getDuplicateLabelWarnings(formData: DialogFormData): PolicyWarning[] {
  const warnings: PolicyWarning[] = [];
  const textLabelIds = new Set(formData.textLabels.textLabels.map(l => l.labelId));
  const imageLabelIds = new Set(formData.imageLabels.imageLabels.map(l => l.labelId));
  const duplicates = [...textLabelIds].filter(id => imageLabelIds.has(id));

  if (duplicates.length > 0) {
    warnings.push({
      category: WarningCategory.CONFIGURATION,
      message: `Some labels appear in both text and image categories: ${duplicates.join(', ')}`,
      suggestion: 'Review label assignments to ensure proper categorization.',
    });
  }

  return warnings;
}

/**
 * Check for best practices warnings
 */
function getBestPracticesWarnings(formData: DialogFormData): PolicyWarning[] {
  const warnings: PolicyWarning[] = [];
  const totalLabels = formData.textLabels.textLabels.length + formData.imageLabels.imageLabels.length;

  if (totalLabels === 0) {
    warnings.push({
      category: WarningCategory.BEST_PRACTICES,
      message: 'Policy has no content labels configured.',
      suggestion: 'Consider adding labels to define what content this policy should monitor. You can add labels later by editing the policy.',
    });
  }

  if (!formData.policyInfo.isEnabled) {
    warnings.push({
      category: WarningCategory.BEST_PRACTICES,
      message: 'Policy is currently disabled.',
      suggestion: 'Remember to enable the policy after creation for it to actively monitor content.',
    });
  }

  return warnings;
}

/**
 * Check for permissive label settings warnings
 */
function getPermissiveLabelWarnings(formData: DialogFormData): PolicyWarning[] {
  const warnings: PolicyWarning[] = [];

  // Check for very permissive text label severities
  const { textLabels } = formData.textLabels;
  const lowSeverityTextLabels = textLabels.filter(label => label.severity <= 1);

  if (lowSeverityTextLabels.length > 0 && lowSeverityTextLabels.length === textLabels.length) {
    warnings.push({
      category: WarningCategory.BEST_PRACTICES,
      message: 'All text label severities are set to minimum values.',
      suggestion: 'Consider if higher severity levels would be more appropriate for effective content moderation.',
    });
  }

  // Check for very permissive image label thresholds
  const { imageLabels } = formData.imageLabels;
  const veryPermissiveImageLabels = imageLabels.filter(label => label.scoreThreshold < 30);

  if (veryPermissiveImageLabels.length > 0 && veryPermissiveImageLabels.length === imageLabels.length) {
    warnings.push({
      category: WarningCategory.BEST_PRACTICES,
      message: 'All image label thresholds are set to very permissive values (< 30%).',
      suggestion: 'Consider if higher thresholds would be more effective for content moderation goals.',
    });
  }

  return warnings;
}

/**
 * Check for inconsistent severity levels
 */
function getSeverityConsistencyWarnings(formData: DialogFormData): PolicyWarning[] {
  const warnings: PolicyWarning[] = [];
  const textSeverities = formData.textLabels.textLabels.map(l => l.severity);

  if (textSeverities.length > 1) {
    const maxSeverity = Math.max(...textSeverities);
    const minSeverity = Math.min(...textSeverities);

    if (maxSeverity - minSeverity > 2) {
      warnings.push({
        category: WarningCategory.BEST_PRACTICES,
        message: 'Text labels have widely varying severity levels.',
        suggestion: 'Review severity consistency to ensure coherent moderation behavior.',
      });
    }
  }

  return warnings;
}

/**
 * Collect all warnings for the dialog form data
 */
function collectAllWarnings(formData: DialogFormData): PolicyWarning[] {
  return [
    ...getConfigurationWarnings(formData),
    ...getDuplicateLabelWarnings(formData),
    ...getBestPracticesWarnings(formData),
    ...getPermissiveLabelWarnings(formData),
    ...getSeverityConsistencyWarnings(formData),
  ];
}

/**
 * Generate warnings for dialog form data
 */
export function generateDialogWarnings(formData: DialogFormData): string[] {
  const warnings = collectAllWarnings(formData);

  // Return formatted warning messages for backward compatibility
  return warnings.map(warning => {
    if (warning.suggestion) {
      return `${warning.message} ${warning.suggestion}`;
    }

    return warning.message;
  });
}

/**
 * Get warnings grouped by category (for enhanced UI)
 */
export function getGroupedDialogWarnings(formData: DialogFormData): Record<WarningCategory, PolicyWarning[]> {
  const warnings = collectAllWarnings(formData);

  // Group warnings by category
  const grouped: Record<WarningCategory, PolicyWarning[]> = {
    [WarningCategory.CONFIGURATION]: [],
    [WarningCategory.BEST_PRACTICES]: [],
    [WarningCategory.ACCESSIBILITY]: [],
  };

  warnings.forEach(warning => {
    grouped[warning.category].push(warning);
  });

  return grouped;
}
